/**
 * Helpers for createJobsFromWizard - directional field name disambiguation
 */
import { centroid } from "@turf/centroid";
import type { Feature, Polygon, MultiPolygon } from "geojson";
import type { Job } from "../../../../../../../models";
import { generateFileName } from "./createJobsFromWizard";
import { FileOptionTypes } from "../../../../../../../utilities/types";

type Coord = [number, number]; // [lng, lat]

/**
 * Disambiguates jobs with identical Grower+Farm+Field combinations by appending
 * directional suffixes (N, S, E, W, etc.) based on boundary centroid positions.
 *
 * Mutates the jobs array in-place.
 */
export function disambiguateDirectionalFieldNames(jobs: Job[]): void {
	const groups = groupJobsByFieldIdentity(jobs);

	for (const group of groups.values()) {
		if (group.length < 2) continue;

		// Compute centroids for jobs that have boundaries
		const jobsWithCentroids: { job: Job; centroid: Coord }[] = [];
		for (const job of group) {
			const centroid = getJobCentroid(job);
			if (centroid) {
				jobsWithCentroids.push({ job, centroid });
			}
		}

		if (jobsWithCentroids.length < 2) continue;

		// Assign directional suffixes
		const suffixes = assignDirectionalSuffixes(jobsWithCentroids.map((j) => j.centroid));

		// Update job field names and file names
		for (let i = 0; i < jobsWithCentroids.length; i++) {
			updateJobFieldName(jobsWithCentroids[i].job, suffixes[i]);
		}
	}
}

/**
 * Groups jobs by case-insensitive Grower+Farm+Field key.
 */
function groupJobsByFieldIdentity(jobs: Job[]): Map<string, Job[]> {
	const groups = new Map<string, Job[]>();

	for (const job of jobs) {
		const key = [
			(job.growerName ?? "").toLowerCase(),
			(job.farmName ?? "").toLowerCase(),
			(job.fieldName ?? "").toLowerCase(),
		].join("|");

		if (!groups.has(key)) {
			groups.set(key, []);
		}
		groups.get(key)!.push(job);
	}

	return groups;
}

/**
 * Computes the centroid of a job's boundary GeoJSON.
 * Returns [lng, lat] or null if boundary is missing/invalid.
 */
function getJobCentroid(job: Job): Coord | null {
	const boundary = job.files?.boundaryGeoJSON ?? job.fileOptions?.boundaryGeoJSON;
	if (!boundary?.features?.length) return null;

	try {
		const feature = boundary.features[0] as Feature<Polygon | MultiPolygon>;
		const center = centroid(feature);
		return center.geometry.coordinates as Coord;
	} catch {
		return null;
	}
}

/**
 * Assigns directional suffixes to a group of jobs based on their centroids.
 * For 2 jobs: uses N/S or E/W based on dominant axis.
 * For 3+: uses 8 compass directions relative to group center.
 */
function assignDirectionalSuffixes(centroids: Coord[]): string[] {
	if (centroids.length === 2) {
		return assignTwoJobSuffixes(centroids[0], centroids[1]);
	}
	return assignMultiJobSuffixes(centroids);
}

/**
 * For exactly 2 jobs: compare on dominant axis.
 * Latitude takes precedence when spreads are equal.
 */
function assignTwoJobSuffixes(a: Coord, b: Coord): string[] {
	const latDiff = Math.abs(a[1] - b[1]);
	const lngDiff = Math.abs(a[0] - b[0]);

	if (latDiff >= lngDiff) {
		// North/South
		return a[1] > b[1] ? [" N", " S"] : [" S", " N"];
	} else {
		// East/West
		return a[0] > b[0] ? [" E", " W"] : [" W", " E"];
	}
}

/**
 * For 3+ jobs: compute group center, then assign 8-direction compass suffixes.
 * Handles collisions by appending an index (e.g., " N", " N2").
 */
function assignMultiJobSuffixes(centroids: Coord[]): string[] {
	// Compute group center
	const centerLng = centroids.reduce((sum, c) => sum + c[0], 0) / centroids.length;
	const centerLat = centroids.reduce((sum, c) => sum + c[1], 0) / centroids.length;
	const center: Coord = [centerLng, centerLat];

	// Assign raw direction for each centroid
	const rawDirections = centroids.map((c) => computeCompassDirection(c, center));

	// Handle collisions: count occurrences per direction
	const directionCounts = new Map<string, number>();
	for (const dir of rawDirections) {
		directionCounts.set(dir, (directionCounts.get(dir) ?? 0) + 1);
	}

	// Build suffixes with collision numbering
	const directionIndexes = new Map<string, number>();
	return rawDirections.map((dir) => {
		const total = directionCounts.get(dir)!;
		if (total === 1) {
			return ` ${dir}`;
		}
		// Multiple jobs in same direction: append index
		const idx = (directionIndexes.get(dir) ?? 0) + 1;
		directionIndexes.set(dir, idx);
		return idx === 1 ? ` ${dir}` : ` ${dir}${idx}`;
	});
}

/**
 * Determines the 8-direction compass bearing from reference to point.
 * Uses atan2(dLng, dLat) mapped to 45-degree sectors.
 */
function computeCompassDirection(point: Coord, reference: Coord): string {
	const dLng = point[0] - reference[0];
	const dLat = point[1] - reference[1];

	// If points are essentially the same location, assign N as default
	if (Math.abs(dLng) < 1e-10 && Math.abs(dLat) < 1e-10) {
		return "N";
	}

	// Angle: 0° = North, 90° = East, etc.
	const angleDeg = Math.atan2(dLng, dLat) * (180 / Math.PI);
	const normalized = (angleDeg + 360) % 360;

	if (normalized >= 337.5 || normalized < 22.5) return "N";
	if (normalized < 67.5) return "NE";
	if (normalized < 112.5) return "E";
	if (normalized < 157.5) return "SE";
	if (normalized < 202.5) return "S";
	if (normalized < 247.5) return "SW";
	if (normalized < 292.5) return "W";
	return "NW";
}

/**
 * Updates a job's field name and regenerates file names in fileOptions.
 */
function updateJobFieldName(job: Job, suffix: string): void {
	const newFieldName = (job.fieldName ?? "") + suffix;
	const growerName = job.growerName ?? "";
	const farmName = job.farmName ?? "";

	// fieldName is readonly on the Job interface, but we need to mutate it here
	// during wizard job creation before the job is finalized
	(job as { fieldName: string }).fieldName = newFieldName;
	job.importFieldName = newFieldName;

	// Regenerate file names in fileOptions
	if (job.fileOptions?.boundaryOptions) {
		for (const optGroup of job.fileOptions.boundaryOptions) {
			for (const opt of optGroup.options) {
				opt.name = generateFileName(growerName, farmName, newFieldName, "boundary");
			}
		}
	}

	if (job.fileOptions?.pointsOptions) {
		for (const optGroup of job.fileOptions.pointsOptions) {
			const suffix = optGroup.type.type === FileOptionTypes.Centroids.type ? "centroids" : "cores";
			for (const opt of optGroup.options) {
				opt.name = generateFileName(growerName, farmName, newFieldName, suffix);
			}
		}
	}

	if (job.fileOptions?.zonesOptions) {
		for (const optGroup of job.fileOptions.zonesOptions) {
			for (const opt of optGroup.options) {
				opt.name = generateFileName(growerName, farmName, newFieldName, "zones");
			}
		}
	}
}
